import { useLocation } from "@tanstack/react-router";
import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import { SidebarMenuBadge, SidebarMenuItem, SidebarMenuSub } from "ui";

import { SidebarSubLink } from "src/components/SidebarSidebarSubLink";
import { useRouteOptions } from "src/contexts/RouteOptionsContext";
import {
  type SidebarItemConfig,
  type SidebarSubItemConfig,
} from "src/staticData";

import { NavButtonV2, type NavButtonProps } from "../layout/NavButton";

interface SidebarNavItemProps {
  item: SidebarItemConfig;
}

function getIsSidebarItemActive(item: SidebarItemConfig, pathname: string) {
  const match = { pathname: pathname };

  if (item.activeWhen?.(match)) {
    return true;
  }

  if (!item.items) {
    return false;
  }

  return item.items.some((subItem) => {
    return getIsSidebarSubItemActive(subItem, pathname);
  });
}

function getIsSidebarSubItemActive(
  item: SidebarSubItemConfig,
  pathname: string,
) {
  const match = { pathname: pathname };

  if (item.activeWhen) {
    return item.activeWhen(match);
  }

  return item.href === pathname;
}
export function SidebarNavItem(props: SidebarNavItemProps) {
  const { t } = useTranslation();
  const routeOptions = useRouteOptions((match) => {
    return getIsSidebarItemActive(props.item, match.pathname);
  });
  const pathname = useLocation({
    select: (loc) => {
      return loc.pathname;
    },
  });
  const isActive = getIsSidebarItemActive(props.item, pathname);
  const navButtonProps: NavButtonProps = {
    icon: <Icon name={props.item.iconName} />,
    text: props.item.text,
    href: props.item.href,
    activeWhen: props.item.activeWhen,
    suffix: props.item.locked ? (
      <Icon name="tabler-lock" className="ml-auto opacity-50" />
    ) : undefined,
  };

  return (
    <SidebarMenuItem>
      <NavButtonV2 {...navButtonProps} />
      {routeOptions?.hasUnsavedChanges ? (
        <SidebarMenuBadge title={t("profileUnsavedChanges")}>
          <span aria-hidden className="size-2 rounded-full bg-amber-500" />
          <span className="sr-only">{t("profileUnsavedChanges")}</span>
        </SidebarMenuBadge>
      ) : null}
      {props.item.items && props.item.items.length > 0 && isActive ? (
        <SidebarMenuSub>
          {props.item.items.map((item) => {
            return <SidebarSubLink item={item} key={item.id ?? item.href} />;
          })}
        </SidebarMenuSub>
      ) : null}
    </SidebarMenuItem>
  );
}
